import { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Button,
	Chip,
	Avatar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Snackbar,
	Alert,
} from '@mui/material';
import {
	Link,
	useOutletContext,
	useLoaderData,
	useNavigate,
	useRevalidator,
} from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import logoMapping from '../../assets/images/services-logos';
import { authedApiFetch } from '../../utils/Api';
import { useComposioOAuth } from '../hooks/useComposioOAuth';

// Add loader function to fetch connections directly
export async function loader({ params }) {
	// TODO: scope which connections to fetch based on user role
	const connections = await authedApiFetch({
		endpoint: `/tenants/${params.tenantId}/connections`,
		method: 'GET',
	});

	return { connections };
}

const ConnectionsList = () => {
	const navigate = useNavigate();
	const revalidator = useRevalidator();
	const [deletingId, setDeletingId] = useState(null);
	const [reconnectingId, setReconnectingId] = useState(null);
	const [notification, setNotification] = useState(null);

	// Handle potential undefined loader data
	const loaderData = useLoaderData();
	// Safely extract connections with a fallback to empty array if undefined
	const connections = loaderData?.connections || [];
	const { tenant, user } = useOutletContext(); // Still need tenant and user for UI

	// Use the Composio OAuth hook for reconnecting
	const { initiateComposioOAuth, oAuthResult } = useComposioOAuth({
		tenantId: tenant.id,
		navigate,
	});

	// Check if user can delete a specific connection
	const canDeleteConnection = (connection) => {
		// Altvia employees can delete all connections
		if (user.role === 'altvia_admin' || user.role === 'altvia_power_user')
			return true;

		// Only individual connections can be deleted by tenant users
		if (connection.scope !== 'individual') return false;

		// Tenant admins can delete any individual connection in their tenant
		if (user.role === 'tenant_admin') return true;

		// Tenant users can delete their own individual connections
		if (user.role === 'tenant_user' && connection.user_id === user.id) {
			return true;
		}

		// Read-only users cannot delete
		return false;
	};

	const handleDeleteConnection = async (connectionId) => {
		if (!window.confirm('Are you sure you want to remove this connection?')) {
			return;
		}

		setDeletingId(connectionId);
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/connections/${connectionId}`,
				method: 'DELETE',
			});

			// Optimistically update the UI
			setNotification({
				message: 'Connection removed successfully',
				severity: 'success',
			});
			
			// Refresh the connections list
			revalidator.revalidate();
		} catch (error) {
			console.error('Failed to delete connection:', error);
			setNotification({
				message: 'Failed to remove connection. Please try again.',
				severity: 'error',
			});
		} finally {
			setDeletingId(null);
		}
	};

	const handleReconnect = async (connection) => {
		setReconnectingId(connection.id);
		try {
			// Initiate OAuth directly without navigating
			initiateComposioOAuth(connection.service_id);
		} catch (error) {
			console.error('Failed to initiate connection:', error);
			setNotification({
				message: 'Failed to start connection. Please try again.',
				severity: 'error',
			});
			setReconnectingId(null);
		}
	};

	// Handle OAuth result
	useEffect(() => {
		if (oAuthResult) {
			setReconnectingId(null);
			if (oAuthResult.success) {
				setNotification({
					message: 'Successfully connected!',
					severity: 'success',
				});
				// Refresh the connections list
				revalidator.revalidate();
			} else {
				setNotification({
					message: oAuthResult.error || 'Failed to connect',
					severity: 'error',
				});
			}
		}
	}, [oAuthResult, revalidator]);

	// Get connection status display properties
	const getStatusProps = (connection_status) => {
		if (!connection_status) return { label: 'Unknown', color: 'default' };

		switch (connection_status) {
			case 'connected':
				return { label: 'Connected', color: 'success' };
			case 'connecting':
				return { label: 'Connecting', color: 'warning' };
			case 'failed':
				return { label: 'Failed', color: 'error' };
			case 'disconnected':
				return { label: 'Disconnected', color: 'default' };
			case 'expired':
				return { label: 'Expired', color: 'error' };
			case 'refreshing':
				return { label: 'Refreshing', color: 'info' };
			default:
				return { label: connection_status, color: 'info' };
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 3,
				}}
			>
				<Typography variant='h5'>Connections</Typography>
				<Box sx={{ display: 'flex', gap: 2 }}>
					{/* Tenant Connection Button - admin only */}
					{(user.role === 'altvia_admin' ||
						user.role === 'altvia_power_user' ||
						user.role === 'tenant_admin') && (
						<Button
							variant='contained'
							color='indigo'
							startIcon={<AddIcon />}
							component={Link}
							to={`/tenants/${tenant.id}/connections/new?scope=tenant`}
						>
							New Tenant Connection
						</Button>
					)}

					{/* Individual Connection Button - all users except tenant read only*/}
					{user.role !== 'tenant_read_only' && (
						<Button
							variant='contained'
							color='indigo'
							startIcon={<AddIcon />}
							component={Link}
							to={`/tenants/${tenant.id}/connections/new?scope=individual`}
						>
							New Individual Connection
						</Button>
					)}
				</Box>
			</Box>

			{connections.length > 0 ? (
				<TableContainer component={Paper}>
					<Table sx={{ minWidth: 650 }} aria-label='connections table'>
						<TableHead>
							<TableRow>
								<TableCell>Service</TableCell>
								<TableCell>Launchpad User</TableCell>
								<TableCell>Connected Account</TableCell>
								<TableCell>Scope</TableCell>
								<TableCell>Environment</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Created</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{connections.map((connection) => {
								const statusProps = getStatusProps(
									connection.connection_status || connection.status
								);

								return (
									<TableRow
										key={connection.id}
										sx={{
											'&:last-child td, &:last-child th': { border: 0 },
										}}
									>
										<TableCell>
											<Box sx={{ display: 'flex', alignItems: 'center' }}>
												{connection.service_logo && (
													<Avatar
														src={logoMapping[connection.service_logo]}
														alt={connection.service_name}
														sx={{
															width: 24,
															height: 24,
															mr: 1,
															bgcolor: 'transparent',
														}}
														variant='square'
													/>
												)}
												{connection.service_name}
											</Box>
										</TableCell>
										<TableCell>{connection.user_email}</TableCell>
										<TableCell>{connection.name}</TableCell>
										<TableCell>
											<Chip
												label={connection.scope || 'unknown'}
												size='small'
												color='secondary'
												variant='outlined'
												sx={{ textTransform: 'capitalize' }}
											/>
										</TableCell>
										<TableCell>
											{connection.environment && (
												<Chip
													label={connection.environment}
													size='small'
													color={
														connection.environment === 'production'
															? 'success'
															: 'info'
													}
													variant='outlined'
													sx={{ textTransform: 'capitalize' }}
												/>
											)}
										</TableCell>
										<TableCell>
											<Chip
												label={statusProps.label}
												size='small'
												color={statusProps.color}
											/>
										</TableCell>
										<TableCell>
											{connection.created_at
												? new Date(connection.created_at).toLocaleDateString()
												: 'N/A'}
										</TableCell>
										<TableCell>
											<Box sx={{ display: 'flex', gap: 1 }}>
												{/* Resume/Retry button for connecting/failed connections */}
												{['connecting', 'failed'].includes(
													connection.connection_status
												) && (
													<Button
														variant='outlined'
														color='primary'
														size='small'
														onClick={() => handleReconnect(connection)}
														disabled={reconnectingId === connection.id}
													>
														{reconnectingId === connection.id ? (
															<CircularProgress size={20} />
														) : connection.connection_status ===
														  'connecting' ? (
															'Resume Connecting'
														) : (
															'Retry Connection'
														)}
													</Button>
												)}

												{/* Remove button for individual connections */}
												{canDeleteConnection(connection) && (
													<Button
														variant='outlined'
														color='error'
														size='small'
														startIcon={<DeleteIcon />}
														onClick={() =>
															handleDeleteConnection(connection.id)
														}
														disabled={deletingId === connection.id}
													>
														{deletingId === connection.id
															? 'Removing...'
															: 'Remove'}
													</Button>
												)}
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Paper sx={{ p: 4, textAlign: 'center' }}>
					<Typography variant='body1' color='text.secondary' gutterBottom>
						No connections found
					</Typography>
					<Typography color='text.secondary' sx={{ mb: 3 }}>
						Click &quot;New Connection&quot; to create your first connection
					</Typography>
				</Paper>
			)}

			{/* Notification Snackbar */}
			<Snackbar
				open={!!notification}
				autoHideDuration={6000}
				onClose={() => setNotification(null)}
			>
				<Alert
					onClose={() => setNotification(null)}
					severity={notification?.severity || 'info'}
					sx={{ width: '100%' }}
				>
					{notification?.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default ConnectionsList;
